import useOptimisticUpdateCustomer from '@/api/mutations/customers/optimistic/useOptimisticUpdateCustomer';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { Checkbox, Field, Tooltip } from '@payaca/ambrosia';

import { CustomersPermissions } from '@payaca/permissions/customers/customers.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import useGetCustomer from '../../../../api/queries/customers/useGetCustomer';

const FileSharingSettings: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();

  /**
   * Queries
   */
  const { customer } = useGetCustomer(customerId);

  /**
   * Mutations
   */
  const { mutate: updateCustomer } = useOptimisticUpdateCustomer(customerId);

  /**
   * State
   */
  const [fileShareSettings, setFileShareSettings] = useState({
    clientPortal: false,
    attachToEmails: false,
  });

  const userCanEditClientPortalShareSettings = useUserHasPermission({
    permissions: [CustomersPermissions.CLIENT_PORTAL_SHARE_SETTINGS],
  });

  useEffect(() => {
    if (userCanEditClientPortalShareSettings) {
      setFileShareSettings({
        clientPortal: true,
        attachToEmails: customer?.fileSharingSettings.attachToEmails ?? false,
      });
    } else {
      setFileShareSettings({
        clientPortal: false,
        attachToEmails: true,
      });
    }
  }, [userCanEditClientPortalShareSettings, customer]);

  const handleAttachToEmailChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    const { checked } = e.target;

    updateCustomer({
      customerId,
      fileSharingSettings: {
        attachToEmails: checked,
      },
    });
  };

  return (
    <div className="typography:prose space-y-2">
      <h4 className="grow">File sharing settings</h4>

      <div className="flex items-center gap-x-2.5">
        <Field.Legacy name="clientPortal">
          <Checkbox
            label="Client Portal"
            checked={fileShareSettings.clientPortal}
            disabled
          />
        </Field.Legacy>

        {!userCanEditClientPortalShareSettings && (
          <Tooltip
            className="flex"
            showArrow
            tooltipContent="Upgrade to the business plan for access to the client portal "
          >
            <UntitledIcon name="info-circle.3" className="h-5 w-5" />
          </Tooltip>
        )}
      </div>

      <Field.Legacy name="attachToEmails">
        <Checkbox
          label="Attach to emails"
          checked={fileShareSettings.attachToEmails}
          disabled={!userCanEditClientPortalShareSettings}
          onChange={handleAttachToEmailChange}
        />
      </Field.Legacy>
    </div>
  );
};

export default FileSharingSettings;
